// ============================================================
// CreatorVault — Homepage Data Constants
// ============================================================

export type ViewType = 'creator' | 'brand';

// ---- Feature Cards ----

export interface Feature {
  icon: string;
  iconClass: 'feature-icon-green' | 'feature-icon-purple' | 'feature-icon-cyan' | 'feature-icon-orange';
  title: string;
  desc: string;
}

export const CREATOR_FEATURES: Feature[] = [
  {
    icon: '💰',
    iconClass: 'feature-icon-green',
    title: 'Earn Per View',
    desc: 'Get paid for every 1,000 views your content generates. No minimums, no guesswork — transparent CPM payouts.',
  },
  {
    icon: '📊',
    iconClass: 'feature-icon-purple',
    title: 'Real-Time Dashboard',
    desc: 'Track your earnings, video performance, and payout history in one place. See exactly how your content performs.',
  },
  {
    icon: '🏆',
    iconClass: 'feature-icon-orange',
    title: 'Leaderboard Rankings',
    desc: 'Compete with other creators. Top performers get featured placement and access to premium campaigns.',
  },
  {
    icon: '🔗',
    iconClass: 'feature-icon-cyan',
    title: 'Connect TikTok & Instagram',
    desc: 'Link your social accounts in seconds. We auto-track views and engagement across platforms.',
  },
  {
    icon: '🎯',
    iconClass: 'feature-icon-purple',
    title: 'Campaign Discovery',
    desc: 'Browse active brand campaigns that match your niche. Apply to ones you love, skip the rest.',
  },
  {
    icon: '⚡',
    iconClass: 'feature-icon-orange',
    title: 'Fast Payouts',
    desc: 'No more waiting 60 days. Get paid on a consistent schedule with full transparency on every dollar.',
  },
];

export const BRAND_FEATURES: Feature[] = [
  {
    icon: '🚀',
    iconClass: 'feature-icon-purple',
    title: 'Launch Campaigns',
    desc: 'Set your budget, define CPM rates, and go live in minutes. Creators start applying immediately.',
  },
  {
    icon: '👥',
    iconClass: 'feature-icon-cyan',
    title: 'Creator Communities',
    desc: 'Build and manage your own roster of trusted creators. Invite top performers to exclusive campaigns.',
  },
  {
    icon: '📈',
    iconClass: 'feature-icon-green',
    title: 'Performance Analytics',
    desc: 'Track every video, view, and engagement metric across all creators in real-time.',
  },
  {
    icon: '💳',
    iconClass: 'feature-icon-orange',
    title: 'Automated Payouts',
    desc: 'Set it and forget it. Payouts are calculated automatically based on verified view counts.',
  },
  {
    icon: '🏅',
    iconClass: 'feature-icon-purple',
    title: 'Creator Leaderboards',
    desc: "See who's driving the most results. Identify your top performers and reward them.",
  },
  {
    icon: '🎛️',
    iconClass: 'feature-icon-cyan',
    title: 'Budget Controls',
    desc: 'Set daily and total budget caps. Pause, adjust, or scale campaigns without leaving the dashboard.',
  },
];

// ---- Stats ----

export interface Stat {
  number: string;
  label: string;
}

export const CREATOR_STATS: Stat[] = [
  { number: '12,000+', label: 'Active Creators' },
  { number: '$2.4M+', label: 'Paid Out' },
  { number: '850M+', label: 'Total Views' },
  { number: '4.8/5', label: 'Creator Rating' },
];

export const BRAND_STATS: Stat[] = [
  { number: '500+', label: 'Brand Partners' },
  { number: '3,200+', label: 'Campaigns Run' },
  { number: '8.4x', label: 'Average ROAS' },
  { number: '<2min', label: 'Campaign Setup' },
];

// ---- Hero Copy ----

export interface HeroData {
  badge: string;
  headline: [string, string];
  subtitle: string;
  cta: string;
  ctaLink: string;
  secondaryCta: string;
  secondaryLink: string;
}

export const CREATOR_HERO: HeroData = {
  badge: 'Now accepting creators',
  headline: ['Create content.', 'Get paid per view.'],
  subtitle:
    'Join brand campaigns on TikTok and Instagram. Earn real money for every 1,000 views your content generates. No follower minimums.',
  cta: 'Start Earning',
  ctaLink: '/auth?type=creator',
  secondaryCta: 'See How It Works',
  secondaryLink: '#features',
};

export const BRAND_HERO: HeroData = {
  badge: '✨ New: 3-day free trial — cancel anytime',
  headline: ['Launch campaigns.', 'Only pay for views.'],
  subtitle:
    'Access 12,000+ UGC creators on TikTok and Instagram. Set your CPM, launch in minutes, and only pay for verified views. Try any plan free for 3 days.',
  cta: 'Start 3-day free trial',
  ctaLink: '/auth?type=brand',
  secondaryCta: 'View Pricing',
  secondaryLink: '/pricing',
};

// ---- FAQ ----

export interface FAQItem {
  q: string;
  a: string;
}

export const FAQ_DATA: FAQItem[] = [
  {
    q: 'How does the CPM model work?',
    a: 'Brands set a CPM (cost per 1,000 views) rate for each campaign. When a creator posts content and it gets views, they earn that rate automatically. Views are tracked in real-time through connected TikTok and Instagram accounts.',
  },
  {
    q: 'Do I need a minimum number of followers?',
    a: 'No. CreatorVault is open to creators of all sizes. What matters is the quality of your content and the views it generates, not your follower count.',
  },
  {
    q: 'How do payouts work for creators?',
    a: 'Earnings are calculated automatically based on verified views. Payouts are processed on a regular schedule — you can track every dollar in your dashboard. No invoicing required.',
  },
  {
    q: 'What platforms are supported?',
    a: 'Currently TikTok and Instagram. Connect your accounts in seconds and we handle the rest — view tracking, engagement metrics, and performance analytics.',
  },
  {
    q: 'How do brands track campaign performance?',
    a: 'Brands get a real-time dashboard showing every video, creator, view count, engagement rate, and cost. Leaderboards highlight top performers, and budget controls let you adjust spend instantly.',
  },
  {
    q: 'Is there a minimum budget for brands?',
    a: 'CreatorVault is designed to work for businesses of all sizes. You can start with a small test campaign and scale up as you see results.',
  },
];

// ---- Comparison Table ----

export type CheckValue = true | false | 'partial';

export interface ComparisonRow {
  feature: string;
  cv: CheckValue;
  traditional: CheckValue;
  other: CheckValue;
}

export const COMPARISON_DATA: ComparisonRow[] = [
  { feature: 'Pay-per-view (CPM) model', cv: true, traditional: false, other: 'partial' },
  { feature: 'Real-time analytics dashboard', cv: true, traditional: false, other: true },
  { feature: 'Auto view tracking (TikTok + IG)', cv: true, traditional: false, other: false },
  { feature: 'Creator leaderboards', cv: true, traditional: false, other: false },
  { feature: 'No follower minimums', cv: true, traditional: false, other: 'partial' },
  { feature: 'Automated payouts', cv: true, traditional: 'partial', other: true },
  { feature: 'Campaign budget controls', cv: true, traditional: true, other: true },
  { feature: 'Creator community management', cv: true, traditional: false, other: 'partial' },
];

// ---- Platform Bento ----

export const BENTO_BAR_HEIGHTS: number[] = [35, 52, 48, 70, 62, 85, 78, 95, 88, 100, 92, 80];

export interface LeaderboardEntry {
  rank: 1 | 2 | 3;
  name: string;
  views: string;
}

export const LEADERBOARD_DATA: LeaderboardEntry[] = [
  { rank: 1, name: '@SarahCreates', views: '2.4M' },
  { rank: 2, name: '@MarkMakes', views: '1.8M' },
  { rank: 3, name: '@JuliContent', views: '1.2M' },
];
